import { ReactNode } from "react";
import { Chip, ChipProps, TooltipProps } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EllipsisTooltip } from "../tooltip";

export type CoreChipProps = Omit<ChipProps, "variant" | "label" | "children"> & {
	variant?: "rectangular" | "rounded";
	mobileStyling?: boolean;
	children: ReactNode;
	chipColor: string;
	fontColor?: string;
	tooltip?: string;
	tooltipProps?: Omit<TooltipProps, "children" | "title" | "classes" | "PopperProps" | "componentsProps">;
};

export default function CoreChip({
	variant = "rounded",
	chipColor,
	fontColor,
	mobileStyling,
	sx,
	children,
	tooltip,
	tooltipProps,
	onDelete,
	deleteIcon,
	...chipProps
}: CoreChipProps) {
	const labelContent = tooltip ? (
		<EllipsisTooltip {...tooltipProps} entry={tooltip}>
			<div>{children}</div>
		</EllipsisTooltip>
	) : (
		<div>{children}</div>
	);

	return (
		<Chip
			{...chipProps}
			onDelete={onDelete}
			deleteIcon={onDelete ? (deleteIcon ?? <CloseIcon />) : undefined}
			label={labelContent}
			sx={{
				height: variant === "rectangular" ? "28px" : "22px",
				minWidth: mobileStyling ? `min(100%, 88px)` : "88px",
				fontSize: mobileStyling ? "13px" : "15px",
				fontWeight: mobileStyling ? 400 : 500,
				borderRadius: variant === "rectangular" ? "4px" : "16px",

				"& .MuiChip-label": {
					whiteSpace: "wrap",
				},

				"& .MuiChip-deleteIcon": {
					width: "16px",
					height: "16px",
					color: fontColor ?? "inherit",
					opacity: 0.6,

					"&:hover": {
						opacity: 1,
						color: fontColor ?? "inherit",
					},
				},

				"&:focus-visible": {
					backgroundColor: "unset",
					outline: "2px solid var(--focus-outline-color-dark)",
					outlineOffset: "2px",
				},

				backgroundColor: chipColor,
				color: fontColor,
				...sx,
			}}
		/>
	);
}
